import { useState, useRef, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Activity, Users, Tag, Radio, Save, Terminal, Send, UserX, Ban, RefreshCw, WifiOff, ChevronRight, History, RotateCw, LineChart as LineChartIcon, MessageSquare,
} from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { DiscordLive } from "@/components/DiscordLive";

const StatCard = ({ icon: Icon, label, value, accent = "primary", testid }) => (
  <div
    data-testid={testid}
    className="relative rounded-lg border border-border bg-[hsl(var(--surface))] p-4 transition-transform duration-200 hover:-translate-y-1"
  >
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">{label}</span>
      <Icon className={`w-4 h-4 ${accent === "secondary" ? "text-secondary" : "text-primary"}`} />
    </div>
    <div className="mt-3 font-head text-2xl font-bold truncate">{value}</div>
  </div>
);

export default function Dashboard() {
  const qc = useQueryClient();
  const [log, setLog] = useState([{ t: "sys", text: "Console RCON prête. Connectez votre serveur dans Paramètres." }]);
  const [cmd, setCmd] = useState("");
  const [broadcast, setBroadcast] = useState("");
  const [busy, setBusy] = useState(false);
  const logRef = useRef(null);

  const { data: status, isLoading, refetch, isFetching } = useQuery({
    queryKey: ["status"],
    queryFn: async () => (await api.get("/server/status")).data,
    refetchInterval: 15000,
  });

  const { data: history = [] } = useQuery({
    queryKey: ["broadcasts"],
    queryFn: async () => (await api.get("/rcon/broadcasts")).data,
  });

  const { data: stats = [] } = useQuery({
    queryKey: ["stats"],
    queryFn: async () => (await api.get("/server/stats?hours=24")).data,
    refetchInterval: 30000,
  });

  // Discord ↔ player bridge: match RCON players to Pal Hub accounts by name / Discord handle.
  const { data: hubUsers = [] } = useQuery({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get("/admin/users")).data,
  });
  const userByName = useMemo(() => {
    const m = new Map();
    for (const u of hubUsers) {
      if (u.ign) m.set(u.ign.toLowerCase().trim(), u);
      if (u.name) m.set(u.name.toLowerCase().trim(), u);
      if (u.discord_username) m.set(u.discord_username.toLowerCase().trim(), u);
    }
    return m;
  }, [hubUsers]);

  const chartData = (stats || []).map((s) => ({
    time: new Date(s.ts).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
    count: s.count,
  }));

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [log]);

  // Player join/leave alerts (client-side diff on each poll)
  const prevPlayersRef = useRef(null);
  useEffect(() => {
    if (!status?.online) { prevPlayersRef.current = null; return; }
    const current = status.players || [];
    const keyOf = (p) => p.steamid || p.playeruid || p.name;
    const currMap = new Map(current.map((p) => [keyOf(p), p]));
    const prev = prevPlayersRef.current;
    if (prev) {
      for (const [k, p] of currMap) {
        if (!prev.has(k)) {
          toast.success(`${p.name || "Un joueur"} a rejoint le serveur`);
          pushLog({ t: "sys", text: `→ ${p.name || "?"} a rejoint` });
        }
      }
      for (const [k, p] of prev) {
        if (!currMap.has(k)) {
          toast(`${p.name || "Un joueur"} a quitté le serveur`);
          pushLog({ t: "sys", text: `← ${p.name || "?"} a quitté` });
        }
      }
    }
    prevPlayersRef.current = currMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const pushLog = (entry) => setLog((l) => [...l, entry]);

  const runCommand = async (command) => {
    if (!command.trim()) return;
    pushLog({ t: "in", text: command });
    setBusy(true);
    try {
      const res = await api.post("/rcon/command", { command });
      pushLog({ t: "out", text: res.data.response || "(pas de réponse)" });
    } catch (e) {
      pushLog({ t: "err", text: e.response?.data?.detail || "Erreur de commande" });
    } finally {
      setBusy(false);
      setCmd("");
    }
  };

  const doAction = async (endpoint, body, okMsg) => {
    setBusy(true);
    try {
      const res = await api.post(endpoint, body);
      pushLog({ t: "out", text: `${okMsg} ${res.data.response ? "→ " + res.data.response : ""}` });
      toast.success(okMsg);
      refetch();
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur";
      pushLog({ t: "err", text: msg });
      toast.error(msg);
    } finally {
      setBusy(false);
      if (endpoint.includes("broadcast")) qc.invalidateQueries({ queryKey: ["broadcasts"] });
    }
  };

  const resendBroadcast = (message) => doAction("/rcon/broadcast", { message }, "Message rediffusé");

  const deleteBroadcast = async (id) => {
    try {
      await api.delete(`/rcon/broadcasts/${id}`);
      qc.invalidateQueries({ queryKey: ["broadcasts"] });
    } catch (e) { console.error("Failed to delete broadcast", e); }
  };

  const online = status?.online;
  const configured = status?.configured;

  return (
    <div className="fade-up space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-primary/80">Tableau de bord</div>
          <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight">
            {status?.name || "Serveur Palworld"}
          </h1>
        </div>
        <button
          data-testid="refresh-status-btn"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} /> Rafraîchir
        </button>
      </div>

      {/* Status banner */}
      <div
        data-testid="server-status-banner"
        className="rounded-lg border border-border bg-[hsl(var(--surface))] p-5 flex items-center gap-4"
      >
        <div
          className={`w-3 h-3 rounded-full ${online ? "bg-secondary pulse-ring" : "bg-destructive"}`}
          style={{ backgroundColor: online ? "#22C55E" : "#EF4444" }}
        />
        <div className="flex-1">
          <div className="font-head font-bold text-lg">
            {isLoading ? "Vérification…" : online ? "EN LIGNE" : "HORS LIGNE"}
          </div>
          <div className="text-sm text-muted-foreground">
            {!configured
              ? "Aucun serveur configuré — ajoutez vos identifiants RCON dans Paramètres."
              : online
              ? `Version ${status?.version || "?"} · ${status?.player_count ?? 0} joueur(s) connecté(s)`
              : status?.error || "Serveur injoignable"}
          </div>
        </div>
        {!online && configured && <WifiOff className="w-6 h-6 text-destructive" />}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-status" icon={Activity} label="Statut" value={online ? "Actif" : "Inactif"} accent={online ? "secondary" : "primary"} />
        <StatCard testid="stat-players" icon={Users} label="Joueurs" value={status?.player_count ?? 0} />
        <StatCard testid="stat-version" icon={Tag} label="Version" value={status?.version || "—"} />
        <StatCard testid="stat-name" icon={Radio} label="Nom serveur" value={status?.name || "—"} />
      </div>

      {/* Discord live presence */}
      <DiscordLive />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Players */}
        <div className="rounded-lg border border-border bg-[hsl(var(--surface))] overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
            <Users className="w-4 h-4 text-primary" />
            <h3 className="font-head font-semibold">Joueurs en ligne</h3>
            <span className="ml-auto text-xs text-muted-foreground">{status?.players?.length ?? 0}</span>
          </div>
          <div className="divide-y divide-border max-h-72 overflow-y-auto scrollbar-thin" data-testid="player-list">
            {status?.players?.length ? (
              status.players.map((p, i) => {
                const hu = userByName.get((p.name || "").toLowerCase().trim());
                return (
                <div key={i} className="group flex items-center gap-3 px-5 py-3 hover:bg-[hsl(var(--surface-hover))] transition-colors">
                  {hu?.picture ? (
                    <img src={hu.picture} alt="" className="w-8 h-8 rounded-full object-cover border border-primary/30" data-testid={`player-avatar-${i}`} />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/15 border border-primary/30 flex items-center justify-center text-xs font-bold text-primary">
                      {p.name?.[0]?.toUpperCase() || "?"}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                      {p.name || "Inconnu"}
                      {hu?.discord_username && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0" style={{ color: "#5865F2", background: "#5865F21a" }} data-testid={`player-discord-${i}`}>
                          <MessageSquare className="w-2.5 h-2.5" /> @{hu.discord_username}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground truncate">{p.steamid || p.playeruid || "—"}</div>
                  </div>
                  <div className="ml-auto flex gap-1 opacity-40 group-hover:opacity-100 transition-opacity">
                    <button
                      data-testid={`kick-player-${i}`}
                      disabled={busy || !p.steamid}
                      onClick={() => doAction("/rcon/kick", { steamid: p.steamid }, `${p.name} expulsé`)}
                      className="p-1.5 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-30"
                      title="Expulser"
                    >
                      <UserX className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`ban-player-${i}`}
                      disabled={busy || !p.steamid}
                      onClick={() => doAction("/rcon/ban", { steamid: p.steamid }, `${p.name} banni`)}
                      className="p-1.5 rounded hover:bg-destructive/20 text-muted-foreground hover:text-destructive transition-colors disabled:opacity-30"
                      title="Bannir"
                    >
                      <Ban className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                );
              })
            ) : (
              <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                {online ? "Aucun joueur connecté." : "Serveur hors ligne."}
              </div>
            )}
          </div>
          {/* Broadcast + quick actions */}
          <div className="p-4 border-t border-border space-y-3">
            <div className="flex gap-2">
              <input
                data-testid="broadcast-input"
                value={broadcast}
                onChange={(e) => setBroadcast(e.target.value)}
                placeholder="Message à diffuser…"
                className="flex-1 rounded-md bg-[hsl(var(--input))] border border-border px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              />
              <button
                data-testid="broadcast-btn"
                disabled={busy || !broadcast.trim()}
                onClick={() => { doAction("/rcon/broadcast", { message: broadcast }, "Message diffusé"); setBroadcast(""); }}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/85 transition-colors disabled:opacity-40"
              >
                <Send className="w-4 h-4" /> Diffuser
              </button>
            </div>
            {history.length > 0 && (
              <div data-testid="broadcast-history">
                <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground mb-2">
                  <History className="w-3.5 h-3.5" /> Historique — renvoyer en un clic
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {history.map((h) => (
                    <span
                      key={h.id}
                      className="group inline-flex items-center gap-1 rounded-full border border-border bg-[hsl(var(--surface-hover))] pl-2.5 pr-1 py-1 text-xs hover:border-primary/50 transition-colors"
                    >
                      <button
                        data-testid={`resend-broadcast-${h.id}`}
                        disabled={busy}
                        onClick={() => resendBroadcast(h.message)}
                        className="inline-flex items-center gap-1 max-w-[180px] truncate text-slate-300 hover:text-primary disabled:opacity-40"
                        title={`Rediffuser : ${h.message}`}
                      >
                        <RotateCw className="w-3 h-3 shrink-0" /> <span className="truncate">{h.message}</span>
                      </button>
                      <button
                        data-testid={`delete-broadcast-${h.id}`}
                        onClick={() => deleteBroadcast(h.id)}
                        className="text-muted-foreground hover:text-destructive opacity-50 group-hover:opacity-100 transition-opacity px-1"
                        title="Retirer de l'historique"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            <button
              data-testid="save-server-btn"
              disabled={busy}
              onClick={() => doAction("/rcon/save", {}, "Sauvegarde du monde lancée")}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-secondary/40 text-secondary px-3 py-2 text-sm font-semibold hover:bg-secondary/10 transition-colors disabled:opacity-40"
              style={{ color: "#22C55E", borderColor: "#22C55E66" }}
            >
              <Save className="w-4 h-4" /> Sauvegarder le monde
            </button>
          </div>
        </div>

        {/* RCON terminal */}
        <div className="rounded-lg border border-border bg-black/60 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
            <Terminal className="w-4 h-4 text-secondary" style={{ color: "#22C55E" }} />
            <h3 className="font-head font-semibold">Console RCON</h3>
          </div>
          <div ref={logRef} data-testid="rcon-log" className="flex-1 dot-grid font-mono text-xs p-4 space-y-1.5 h-72 overflow-y-auto scrollbar-thin">
            {log.map((l, i) => (
              <div key={i} className="whitespace-pre-wrap break-words">
                {l.t === "in" && <span className="text-primary">&gt; {l.text}</span>}
                {l.t === "out" && <span className="text-secondary" style={{ color: "#4ade80" }}>{l.text}</span>}
                {l.t === "err" && <span className="text-destructive" style={{ color: "#f87171" }}>✕ {l.text}</span>}
                {l.t === "sys" && <span className="text-muted-foreground">{l.text}</span>}
              </div>
            ))}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); runCommand(cmd); }}
            className="flex items-center gap-2 border-t border-border px-3 py-2 bg-black/40"
          >
            <ChevronRight className="w-4 h-4 text-secondary shrink-0" style={{ color: "#22C55E" }} />
            <input
              data-testid="rcon-command-input"
              value={cmd}
              onChange={(e) => setCmd(e.target.value)}
              placeholder="Ex: ShowPlayers, Info, Broadcast Hello…"
              className="flex-1 bg-transparent font-mono text-xs text-foreground focus:outline-none"
            />
            <button
              data-testid="rcon-send-btn"
              type="submit"
              disabled={busy}
              className="text-xs font-semibold text-primary hover:text-glow disabled:opacity-40"
            >
              ENVOYER
            </button>
          </form>
        </div>
      </div>

      {/* Player count chart */}
      <div className="rounded-lg border border-border bg-[hsl(var(--surface))] overflow-hidden" data-testid="stats-card">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-border">
          <LineChartIcon className="w-4 h-4 text-primary" />
          <h3 className="font-head font-semibold">Fréquentation (24 h)</h3>
          <span className="ml-auto text-xs text-muted-foreground">{chartData.length} points</span>
        </div>
        {chartData.length > 1 ? (
          <div className="p-4">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="playerGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00CCFF" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#00CCFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(226 24% 20%)" />
                <XAxis dataKey="time" tick={{ fill: "hsl(215 20% 65%)", fontSize: 11 }} stroke="hsl(226 24% 25%)" minTickGap={40} />
                <YAxis allowDecimals={false} tick={{ fill: "hsl(215 20% 65%)", fontSize: 11 }} stroke="hsl(226 24% 25%)" />
                <Tooltip
                  contentStyle={{ background: "hsl(226 24% 9%)", border: "1px solid hsl(226 24% 22%)", borderRadius: 8, color: "#fff", fontSize: 12 }}
                  labelStyle={{ color: "hsl(215 20% 65%)" }}
                  formatter={(v) => [`${v} joueur(s)`, "Connectés"]}
                />
                <Area type="monotone" dataKey="count" stroke="#00CCFF" strokeWidth={2} fill="url(#playerGrad)" dot={{ r: 2, fill: "#00CCFF" }} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground" data-testid="stats-empty">
            <LineChartIcon className="w-8 h-8 mx-auto mb-3 opacity-40" />
            Le graphique se remplit automatiquement pendant que ton serveur est en ligne.<br />
            Connecte ton serveur et laisse le tableau de bord ouvert pour suivre la fréquentation.
          </div>
        )}
      </div>
    </div>
  );
}
